import React, { useEffect, useMemo, useState } from "react";
import DashboardPage from "./components/DashboardPage";
import TransactionsPage from "./components/TransactionsPage";
import PendingPage from "./components/PendingPage";
import TicketsPage from "./components/TicketsPage";
import TenantsPage from "./components/TenantsPage";
import UsersPage from "./components/UsersPage";
import SuppliersPage from "./components/SuppliersPage";
import RendicontazionePage from "./components/RendicontazionePage"; // ✅ NEW
import "./App.css";

/** Icone (no librerie) */
const ICONS = {
  dashboard: "📊",
  transactions: "💳",
  rendicontazione: "🧾", // ✅ NEW
  pending: "⏳",
  assistenza: "🎧",
  tenants: "🏢",
  users: "👤",
  suppliers: "🚚",
};

/** ✅ Ruoli robusti: user.roles (array) + fallback user.role */
function normalizeRoles(user) {
  const roles = Array.isArray(user?.roles) ? [...user.roles] : [];
  if (roles.length === 0 && user?.role) roles.push(user.role);

  return roles
    .map((r) =>
      String(r || "")
        .trim()
        .toLowerCase()
        .replace(/_/g, " ")
    )
    .filter(Boolean);
}

function hasRole(user, role) {
  const target = String(role || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");
  return normalizeRoles(user).includes(target);
}

function primaryRoleLabel(user) {
  const roles = normalizeRoles(user);
  const r = roles[0] || "utente";
  return r.charAt(0).toUpperCase() + r.slice(1);
}

/** ✅ pulizia token */
function normalizeToken(raw) {
  if (!raw) return null;
  let t = String(raw).trim();
  if (/^bearer\s+/i.test(t)) t = t.replace(/^bearer\s+/i, "").trim();
  if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
    t = t.slice(1, -1).trim();
  }
  return t || null;
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  const [page, setPage] = useState("dashboard");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // ✅ bootstrap da localStorage
  useEffect(() => {
    try {
      const tRaw = localStorage.getItem("sos_token") || localStorage.getItem("token");
      const t = normalizeToken(tRaw);

      const meRaw = localStorage.getItem("sos_me");
      const me = meRaw ? JSON.parse(meRaw) : null;

      setToken(t || null);
      setUser(me || null);
    } catch {
      setToken(null);
      setUser(null);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("sos_token");
    localStorage.removeItem("sos_me");
    localStorage.removeItem("token");
    localStorage.removeItem("me");
    localStorage.removeItem("access_token");
    localStorage.removeItem("id_token");

    setUser(null);
    setToken(null);
    setPage("dashboard");
    setMobileNavOpen(false);

    window.location.href = "https://auth.soscontrol.it";
  };

  // ✅ permessi
  const canSeePending = (u) =>
    hasRole(u, "segretario") ||
    hasRole(u, "aiuto segretario") ||
    hasRole(u, "tesoriere") ||
    hasRole(u, "aiuto tesoriere") ||
    hasRole(u, "admin") ||
    hasRole(u, "developer");

  const canSeeSuppliers = (u) =>
    hasRole(u, "segretario") ||
    hasRole(u, "aiuto segretario") ||
    hasRole(u, "tesoriere") ||
    hasRole(u, "aiuto tesoriere") ||
    hasRole(u, "admin") ||
    hasRole(u, "developer");

  const canSeeUsers = (u) => hasRole(u, "admin") || hasRole(u, "developer");
  const canSeeTenants = (u) => hasRole(u, "developer");

  const navItems = useMemo(() => {
    if (!user) return [];

    const items = [
      { key: "dashboard", label: "Dashboard" },
      { key: "transactions", label: "Transazioni" },
      { key: "rendicontazione", label: "Rendicontazione" }, // ✅ NEW
    ];

    if (canSeePending(user)) items.push({ key: "pending", label: "In attesa" });

    items.push({ key: "assistenza", label: "Assistenza" });

    if (canSeeTenants(user)) items.push({ key: "tenants", label: "Tenant" });
    if (canSeeUsers(user)) items.push({ key: "users", label: "Utenti" });
    if (canSeeSuppliers(user)) items.push({ key: "suppliers", label: "Fornitori" });

    return items;
  }, [user]);

  useEffect(() => setMobileNavOpen(false), [page]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e) => e.key === "Escape" && setMobileNavOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);

  const activeLabel = useMemo(() => navItems.find((i) => i.key === page)?.label || "Menu", [navItems, page]);

  // ✅ se manca token/user
  if (!user || !token) {
    return (
      <div className="App">
        <header className="App-header">
          <div className="appHeader__left">
            <h1 className="appTitle">SOSControl - Contabilità</h1>
          </div>
        </header>

        <main className="appMain" style={{ padding: 24 }}>
          <h2>Accesso richiesto</h2>
          <p>Devi accedere da auth.soscontrol.it per usare Contabilità.</p>
          <a href="https://auth.soscontrol.it">Vai al login</a>
        </main>
      </div>
    );
  }

  const roles = normalizeRoles(user);

  return (
    <div className="App">
      {/* HEADER */}
      <header className="App-header">
        <div className="appHeader__left">
          <button
            type="button"
            className="mobileNavBtn"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Apri menu"
            title="Menu"
          >
            ☰
          </button>

          <div className="appHeader__titles">
            <h1 className="appTitle">Rendiconto Contabile</h1>
            <div className="appPageTitle" aria-hidden>
              {activeLabel}
            </div>
          </div>
        </div>

        <div className="appHeader__right">
          <span
            className={`roleBadge roleBadge--${primaryRoleLabel(user).toLowerCase()}`}
            title={`Ruoli: ${roles.join(", ")}`}
          >
            <span className="roleBadge__dot" aria-hidden />
            {primaryRoleLabel(user)}
          </span>

          <button className="logoutPill" onClick={handleLogout} type="button">
            <span aria-hidden className="logoutPill__icon">⏻</span>
            Logout
          </button>
        </div>
      </header>

      {/* LAYOUT */}
      <div className="appShell">
        {/* SIDEBAR desktop */}
        <aside className="sideNav" aria-label="Navigazione">
          <div className="sideNav__top">
            <div className="sideNav__title">Menu</div>
            <div className="sideNav__hint">Sezioni</div>
          </div>

          <nav className="sideNav__list">
            {navItems.map((item) => {
              const active = page === item.key;
              return (
                <button
                  key={item.key}
                  className={`sideNav__item ${active ? "is-active" : ""}`}
                  onClick={() => setPage(item.key)}
                  type="button"
                  aria-current={active ? "page" : undefined}
                >
                  <span className="sideNav__icon" aria-hidden>
                    {ICONS[item.key] || "•"}
                  </span>
                  <span className="sideNav__label">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* MOBILE DRAWER NAV */}
        {mobileNavOpen && (
          <div className="mobileNavOverlay" role="dialog" aria-modal="true" aria-label="Menu">
            <div className="mobileNavDrawer" onClick={(e) => e.stopPropagation()}>
              <div className="mobileNavHeader">
                <div>
                  <div className="mobileNavTitle">Menu</div>
                  <div className="mobileNavSub">
                    {roles.join(", ")} · {user?.username || user?.name || user?.email || ""}
                  </div>
                </div>

                <button
                  type="button"
                  className="mobileNavClose"
                  onClick={() => setMobileNavOpen(false)}
                  aria-label="Chiudi menu"
                  title="Chiudi"
                >
                  ✕
                </button>
              </div>

              <nav className="mobileNavList">
                {navItems.map((item) => {
                  const active = page === item.key;
                  return (
                    <button
                      key={item.key}
                      className={`mobileNavItem ${active ? "is-active" : ""}`}
                      onClick={() => setPage(item.key)}
                      type="button"
                      aria-current={active ? "page" : undefined}
                    >
                      <span className="mobileNavIcon" aria-hidden>{ICONS[item.key] || "•"}</span>
                      <span className="mobileNavLabel">{item.label}</span>
                      {active && <span className="mobileNavActive" aria-hidden>✓</span>}
                    </button>
                  );
                })}
              </nav>

              <div className="mobileNavFooter">
                <button type="button" className="mobileLogout" onClick={handleLogout}>
                  ⏻ Logout
                </button>
              </div>
            </div>

            <button
              className="mobileNavBackdrop"
              type="button"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Chiudi menu"
            />
          </div>
        )}

        {/* CONTENT */}
        <main className="appMain">
          {page === "dashboard" && <DashboardPage token={token} user={user} />}
          {page === "transactions" && <TransactionsPage token={token} user={user} />}

          {page === "rendicontazione" && <RendicontazionePage token={token} user={user} />}

          {page === "pending" && canSeePending(user) && <PendingPage token={token} user={user} />}

          {page === "assistenza" && <TicketsPage token={token} user={user} />}

          {page === "tenants" && canSeeTenants(user) && <TenantsPage token={token} />}

          {page === "users" && canSeeUsers(user) && <UsersPage token={token} user={user} />}

          {page === "suppliers" && canSeeSuppliers(user) && <SuppliersPage token={token} user={user} />}
        </main>
      </div>
    </div>
  );
}

export default App;
